/**
 * Phase 6 — Note list / sign / amend helpers.
 * Does not write Encounter.reasonCode or delete localStorage.
 */
import { assertEncounterWritable } from "@/lib/encounterApi";
import { api } from "@/lib/orgApi";

/** Kind of the Note's open draft, or null when there is nothing editable to write. */
export function draftVersionKind(note, versionId = null) {
  const version = note?.currentVersion;
  if (!version || version.status !== "draft") return null;
  if (versionId && version.id !== versionId) return null;
  return version.kind || null;
}

export async function listNoteTypes() {
  const { data } = await api.get("/note-types");
  return data.items || [];
}

export async function listEncounterNotes(encounterId) {
  if (!encounterId) return [];
  const { data } = await api.get("/notes", { params: { encounter: encounterId } });
  return data.items || [];
}

export async function listPatientNotes(patientId) {
  if (!patientId) return [];
  const { data } = await api.get("/notes", { params: { patient: patientId } });
  return data.items || [];
}

export async function getNote(noteId) {
  const { data } = await api.get(`/notes/${noteId}`);
  return data;
}

export async function createNoteIdentity({ encounterId, noteTypeId, title }) {
  await assertEncounterWritable(encounterId);
  const body = { encounterId, noteTypeId };
  if (title && String(title).trim()) body.title = String(title).trim();
  const { data } = await api.post("/notes", body);
  return data;
}

export async function createOriginalDraft(noteId, payload = {}) {
  const note = await getNote(noteId);
  await assertEncounterWritable(note.encounterId);
  const { data } = await api.post(`/notes/${noteId}/versions`, payload);
  return data;
}

export async function updateDraftVersion(noteId, versionId, payload) {
  const note = await getNote(noteId);
  await assertEncounterWritable(note.encounterId, { versionKind: draftVersionKind(note, versionId) });
  const { data } = await api.put(`/notes/${noteId}/versions/${versionId}`, payload);
  return data;
}

export async function signNote(noteId) {
  const note = await getNote(noteId);
  await assertEncounterWritable(note.encounterId, { versionKind: draftVersionKind(note) });
  const { data } = await api.post(`/notes/${noteId}/sign`);
  return data;
}

export async function amendNote(noteId, kind) {
  const note = await getNote(noteId);
  // Creating an addendum is not exempt from the completed-encounter lock.
  await assertEncounterWritable(note.encounterId, {
    versionKind: kind === "addendum" ? null : kind,
  });
  const { data } = await api.post(`/notes/${noteId}/amend`, { kind });
  return data;
}

export async function listNoteVersions(noteId) {
  const { data } = await api.get(`/notes/${noteId}/versions`);
  return data.items || [];
}

export async function getNoteVersion(noteId, versionId) {
  const { data } = await api.get(`/notes/${noteId}/versions/${versionId}`);
  return data;
}

export function isVisitNote(note) {
  return note?.noteType?.code === "visit";
}

/** Identity-only Notes (no persisted Version) stay hidden from the Encounter list. */
export function noteHasPersistedVersion(note) {
  return Boolean(
    note?.currentVersionId ||
      note?.latestSignedVersionId ||
      note?.currentVersion?.id,
  );
}

export function visibleEncounterNotes(notes = []) {
  return (notes || []).filter(noteHasPersistedVersion);
}

export function shouldShowNotesEmptyState(notes = [], selectedNote = null) {
  return !selectedNote && visibleEncounterNotes(notes).length === 0;
}

export function currentVersionOf(note) {
  return note?.currentVersion || null;
}

export function noteDisplayStatus(note) {
  const version = currentVersionOf(note);
  if (version?.status === "draft") return "draft";
  if (version?.status === "signed") return "signed";
  if (note?.status === "signed" && !version) return "signed";
  return "draft";
}

export function noteIsReadOnly(note) {
  return noteDisplayStatus(note) === "signed";
}

export function hasVersionHistory(note) {
  const version = currentVersionOf(note);
  return Boolean(note?.latestSignedVersionId || (version?.versionNumber && version.versionNumber > 1));
}

export function currentVisitNote(notes = [], selectedNoteId = null) {
  const selected = (notes || []).find((note) => note.id === selectedNoteId);
  if (selected && isVisitNote(selected)) return selected;
  return (notes || []).find((note) => isVisitNote(note)) || null;
}

export function canSignNote(note) {
  const version = currentVersionOf(note);
  if (!note || !version || version.status !== "draft") return false;
  const content = version.content || {};
  const text = String(content.text || "").trim();
  const diagnoses = version.diagnoses || [];
  const cpt = version.cpt || [];
  return Boolean(text || diagnoses.length || cpt.length);
}

export async function persistNoteText(noteId, text, format = "free_text") {
  const note = await getNote(noteId);
  await assertEncounterWritable(note.encounterId, { versionKind: draftVersionKind(note) });
  const version = note.currentVersion;
  const content = { format, text: text || "" };
  if (version?.status === "draft" && version.id) {
    const { data } = await api.put(`/notes/${noteId}/versions/${version.id}`, { content });
    return data;
  }
  if (!version) {
    const { data } = await api.post(`/notes/${noteId}/versions`, { content });
    return data;
  }
  return { skipped: true, reason: "signed", id: version.id };
}

export function versionKindLabel(kind) {
  if (kind === "addendum") return "Addendum";
  if (kind === "correction") return "Correction";
  return "Original";
}
